// Why: permission-model 03-groups §6 — every closed enum is declared in exactly one module and
// imported everywhere else (server, client, tests). The database CHECK constraints mirror these
// lists and a test asserts they agree (06-conformance T6). Values are stable identifiers; the
// *_LABELS maps are presentation only and must never be used as keys.

export function isOneOf<const T extends readonly string[]>(values: T, candidate: unknown): candidate is T[number] {
  return typeof candidate === 'string' && (values as readonly string[]).includes(candidate);
}

// --- Scopes ------------------------------------------------------------------------------------

/** The single tenancy unit. Scope *selection* defaults to it; admission still requires a grant. */
export const DEFAULT_SCOPE_ID = 'centro';
export type ScopeId = string;

export const SCOPE_CAPABILITIES = ['member'] as const;
export type ScopeCapability = (typeof SCOPE_CAPABILITIES)[number];
export const SCOPE_CAPABILITY_LABELS: Readonly<Record<ScopeCapability, string>> = {
  member: 'Member',
};

// --- Administrative sections (permission-model 03-groups §3.6) ---------------------------------

export const ADMIN_SECTIONS = ['users', 'audit'] as const;
export type AdminSection = (typeof ADMIN_SECTIONS)[number];
export const ADMIN_SECTION_LABELS: Readonly<Record<AdminSection, string>> = {
  users: 'Users',
  audit: 'Audit log',
};
/**
 * Why: §3.6 requires each section to declare its reach so the group editor can label it. With a
 * single scope every section reads the whole instance, and the UI must say so.
 */
export const ADMIN_SECTION_REACH: Readonly<Record<AdminSection, 'instance' | 'scope'>> = {
  users: 'instance',
  audit: 'instance',
};
/**
 * What holding a section's `view` actually shows, said where the grant is made (permission-model
 * 05-administration §4: the operator must not have to guess what a tick reaches). The audit note is
 * load-bearing: rows about users' own meetings, to-dos and Fellow and Slack connections reach a
 * delegate by state and identifier only (audit/audit.ts presentAuditEventForDelegate), never by
 * content.
 */
export const ADMIN_SECTION_DESCRIPTIONS: Readonly<Record<AdminSection, string>> = {
  users: 'Every account: name, email address, state, and its rights with their sources.',
  audit: 'Every audit row: who did what, to which record, with what outcome. Other users\' meetings, to-dos, and Fellow and Slack connections appear by state and identifier only, never by title or text. Also the Costs page (every AI call: whose, when and what it cost) and the Queue page (whose jobs run and wait now, by kind and time, never what they are about).',
};

export const ADMIN_ACTIONS = ['view', 'act'] as const;
export type AdminAction = (typeof ADMIN_ACTIONS)[number];
export const ADMIN_ACTION_LABELS: Readonly<Record<AdminAction, string>> = {
  view: 'View',
  act: 'Act',
};

// --- Global capabilities (permission-model 03-groups §3.9) -------------------------------------

// Why `executive_intelligence` last (migration 0053; the owner's request of 2026-10-04 and his sixteen picks E1–E16,
// escalation 20261005-000818-executive-intelligence-an-organisation-report-sh; ADR 0019): a fifth capability, an elevated
// processing mode (03-groups §3.9) carried by groups only, AND-ed with its own deployment floor EXECUTIVE_MODE
// (authz/floors.ts) — the superuser holds it wherever that floor is met (E6). Existing groups gain nothing
// (05-administration "Existing groups do not gain it"); granting it is a superuser hard rail (no delegate route).
export const GLOBAL_CAPABILITIES = ['fellow_connect', 'ai_summaries', 'slack_connect', 'slack_analysis', 'executive_intelligence'] as const;
export type GlobalCapability = (typeof GLOBAL_CAPABILITIES)[number];
export const GLOBAL_CAPABILITY_LABELS: Readonly<Record<GlobalCapability, string>> = {
  fellow_connect: 'Connect Fellow',
  ai_summaries: 'AI summaries',
  slack_connect: 'Slack connect',
  slack_analysis: 'Slack analysis',
  executive_intelligence: 'Executive intelligence',
};
export const GLOBAL_CAPABILITY_DESCRIPTIONS: Readonly<Record<GlobalCapability, string>> = {
  // Why it also ticks off (owner decision O1(b), 2026-09-30, "Everyone who connects Fellow"): Calis completes in
  // Fellow, with the person's own key, the action items whose to-dos they complete — under this grant, with its
  // floor and the deployment's FELLOW_WRITE_MODE floor above it; there is no capability of its own.
  fellow_connect: "Connect a Fellow account, import its meetings automatically, and tick off in Fellow the action items completed in Calis, with the person's own key.",
  // Why the climate and the report (design §2.14 item 6, verbatim; the owner's picks E2, E3, E4 and E7 of 2026-10-04,
  // ADR 0019): where executive intelligence is on, the summary this grant sends a transcript for also answers one closed
  // climate word, and the summary goes once a day into the executives' report — the grant's purpose widened, said here,
  // where an administrator reads it before granting. The qualifier is said twice (2026-10-06, ADR 0019 Open 8): once
  // before the climate word and once before the report, so that neither clause reads as true while the floor is off.
  ai_summaries:
    "Send meeting transcripts to the AI provider to generate summaries and to-dos, and — where executive intelligence is on — one closed word on each discussion's climate; where it is on, the summary, its key points, decisions and climate word, with the names of projects the person shares, then go once a day into the executives' organisation report.",
  slack_connect: 'Connect a Slack account, leave channels or direct messages out of what Calis reads, and keep the channel list current.',
  // Why it names the daily look (ADR 0016; ADR 0006 amendment of 2026-10-01): with SLACK_SWEEP_MODE on, the same two
  // Slack grants have Calis read the person's whole Slack day and act on their list — the grant's line says so before an
  // administrator gives it, as fellow_connect's says it ticks off in Fellow (O1(b)). Why the last sentence (design §2.14
  // item 6, verbatim; the owner's pick E3 of 2026-10-04, ADR 0019): where executive intelligence is on, each check and each
  // daily look also answers one closed climate word, counted into the executives' pulse — said before the grant is given.
  // Why "whether or not … AI summaries" (the owner's ruling of 2026-10-13 on ADR 0019's Open 56, "Count them too"): the
  // report reads the marks of every admitted member who holds both Slack grants and a live connection, so these two grants
  // alone put the person's Slack climate into the pulse — said here, where an administrator reads it before granting.
  slack_analysis:
    "Send related Slack messages (any channel, DM or group chat the person can see, less what they leave out), with the meeting summary, to the AI provider to find extra context, inconsistencies and opportunities. Where the daily Slack look is on, also read the person's Slack day once a day (every public channel included), send what concerns them to the AI provider with their open to-dos and recent meetings, and add 'From Slack' to-dos, notes and 'Mark done' suggestions to their list. Where executive intelligence is on, each check and each daily look also answers one closed climate word, counted unattributed into the executives' organisation pulse, whether or not the person also holds AI summaries.",
  // Why every clause (design §2.1, verbatim; the owner's picks E2, E3, E4, E5, E7, E13): the administrator reads this
  // before granting the one capability that reads across members — what the report is made from, what it never holds,
  // that there is no opt-out, and where members are told and the holders named. Why "however few people's meetings" where
  // it said "never fewer than three people's meetings at once" (the owner's rulings of 2026-10-12, "Remove them
  // everywhere", "Count them" and "Names only where three"): the grant is read against what the report now does. Why the
  // Slack clause names every member connected with both Slack grants, AI summaries or not, where it said "from each
  // member's Slack checks and daily looks" beside "Every member whose summaries Calis makes contributes" (the owner's ruling
  // of 2026-10-13 on ADR 0019's Open 56, "Count them too"): the report reads exactly those members' marks now.
  executive_intelligence:
    "Read the organisation report and pulse Calis writes once a day from every contributing member's meeting summaries — their AI-made summary, key points, decisions and one climate word, and the names of projects shared between two or more people — and from a wordless climate mark of each Slack check and daily look of every member who holds both Slack grants and has Slack connected, whether or not Calis makes their summaries — themes, risks, blockers and open decisions for the organisation as a whole, never a transcript, a message, a title, a name, a to-do, a note, a brief or a project one person alone holds — written however few people's meetings there are, one person's and the executives' own included, and naming a shared project only where at least three people's meetings stand behind it. Every member whose summaries Calis makes contributes their summaries, and every member connected to Slack with both Slack grants their Slack climate; members are told at sign-in and in Settings → Your data, where the executives who hold this are named.",
};
/**
 * Why: a capability whose action also needs another one (a Slack analysis reads Slack, which is
 * what `slack_connect` governs, and then sends to the AI provider). This is NOT a combine rule: the
 * resolver still resolves each capability on its own (OR across groups, AND its floor), and every
 * enforcement point that needs both checks both as independent positive gates. The map exists only
 * so the group editor can warn about a grant that cannot be used alone (05-administration §4) and so
 * the interface can name the missing grant. Ruled by the owner, 2026-09-26 (escalation
 * 20260926-002133, E4).
 */
export const CAPABILITY_REQUIRES: Readonly<Partial<Record<GlobalCapability, readonly GlobalCapability[]>>> = {
  slack_analysis: ['slack_connect'],
};

// --- Subjects ----------------------------------------------------------------------------------

export const SUBJECT_PROVIDERS = ['google', 'dev'] as const;
export type SubjectProvider = (typeof SUBJECT_PROVIDERS)[number];
export const SUBJECT_PROVIDER_LABELS: Readonly<Record<SubjectProvider, string>> = {
  google: 'Google Workspace',
  dev: 'Development persona',
};

export const SUBJECT_STATUSES = ['pending', 'approved', 'denied', 'suspended'] as const;
export type SubjectStatus = (typeof SUBJECT_STATUSES)[number];
/** The precise lifecycle label — for a pill's title and prose, not for the pill itself. */
export const SUBJECT_STATUS_LABELS: Readonly<Record<SubjectStatus, string>> = {
  pending: 'Pending approval',
  approved: 'Approved',
  denied: 'Denied',
  suspended: 'Suspended',
};
/**
 * The state word a pill shows, from the shared vocabulary (design system 05-content §6): an
 * approved account is Active, a suspended one is present but not in use (Inactive), a request is
 * Pending, an admission an administrator declined is Denied (v8.1.0). Every surface that draws a status pill reads this map, so they cannot drift apart.
 */
export const SUBJECT_STATE_WORDS: Readonly<Record<SubjectStatus, string>> = {
  pending: 'Pending',
  approved: 'Active',
  denied: 'Denied',
  suspended: 'Inactive',
};

// --- Jobs --------------------------------------------------------------------------------------

/** Appended, never reordered: the `jobs.kind` CHECK lists these in this order (migrations 0004, 0005, 0012, 0020, 0029, 0031, 0035, 0052, 0053). */
// Why `fellow.items` (migration 0029): ticking off in Fellow — one job per person drains what Calis owes Fellow
// for their holdings, with their own key. Why `slack.sweep` after it (migration 0031): the daily Slack sweep, one
// job per person per day. Why `todo.fellow_match` after that (migration 0035): Calis's own look, for one person and
// one meeting, at which of their Fellow items are the same work as their Calis AI to-dos (ADR 0015, Stage 2). Why
// `todo.project_sort` last (migration 0052; owner ruling 11 of 2026-10-04, ADR 0018): the fifth AI call, which sorts a
// person's open to-dos that are in no project into their projects — scheduled once a day when that set changed, or
// asked for with "Sort my to-dos now". Why `executive.report` last (migration 0053; ADR 0019; design §2.9): the
// executives' one shared organisation report a day, run as an admitted holder of `executive_intelligence` (its executor,
// src/server/executive/report-job.ts, since the design's stage 4). The CHECK in `jobs` follows this list's order.
export const JOB_KINDS = ['fellow.sync', 'meeting.summarise', 'slack.sync', 'meeting.slack_context', 'assistant.brief', 'todo.link_scan', 'fellow.items', 'slack.sweep', 'todo.fellow_match', 'todo.project_sort', 'executive.report'] as const;
export type JobKind = (typeof JOB_KINDS)[number];
export const JOB_KIND_LABELS: Readonly<Record<JobKind, string>> = {
  'fellow.sync': 'Fellow sync',
  'meeting.summarise': 'Meeting summary',
  'slack.sync': 'Slack channel list refresh',
  'meeting.slack_context': 'Slack check',
  'assistant.brief': 'Assistant brief',
  // Why "(shared)": the scan compares several people's to-dos of one meeting (ADR 0014); the person the
  // job runs as is only the one whose change asked for it.
  'todo.link_scan': 'Linking to-dos (shared)',
  'fellow.items': 'Fellow tick-off',
  'slack.sweep': 'Daily Slack sweep',
  'todo.fellow_match': "Matching Fellow's items",
  // 05-content §6: a running process is named by its gerund, as the Fellow look is.
  'todo.project_sort': 'Sorting to-dos into projects',
  // The design's own label (§2.8): the job writes one shared report for the organisation, never a person's.
  'executive.report': 'Executive report',
};

// `skipped` (migration 0050; 05-content §6): considered, and not applicable — nothing was done. Appended last, as the CHECK is.
export const JOB_STATUSES = ['queued', 'running', 'done', 'failed', 'refused', 'skipped'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];
/** 05-content §6 (v8.1.0): waiting to start is Pending, finished well is Completed, declined by policy is Refused. */
export const JOB_STATUS_LABELS: Readonly<Record<JobStatus, string>> = {
  queued: 'Pending',
  running: 'Running',
  done: 'Completed',
  failed: 'Failed',
  refused: 'Refused',
  skipped: 'Skipped',
};

/**
 * Why a due job is not claimed yet (ADR 0013, the worker's claim rules, src/server/jobs/claim-rules.ts),
 * in the rules' precedence: the first that applies is the one reported.
 */
export const JOB_WAIT_REASONS = [
  'no_free_slot',
  'person_busy',
  'links_busy',
  'ai_cooling',
  'ai_slots_full',
  'slack_checks_full',
  'slack_sweeps_full',
  'slack_syncs_full',
  'bulk_slots_full',
  'refresh_in_flight',
] as const;
export type JobWaitReason = (typeof JOB_WAIT_REASONS)[number];
export const JOB_WAIT_REASON_LABELS: Readonly<Record<JobWaitReason, string>> = {
  no_free_slot: 'Every worker slot is busy',
  person_busy: 'The same person\'s job in progress comes first',
  links_busy: 'Another linking of to-dos is running',
  ai_cooling: 'The AI service asked Calis to wait',
  ai_slots_full: 'Every slot for AI work is busy',
  slack_checks_full: 'Another Slack check is running',
  slack_sweeps_full: 'Another daily Slack sweep is running',
  slack_syncs_full: 'Another Slack channel list refresh is running',
  bulk_slots_full: 'The slot for bulk work is busy',
  refresh_in_flight: 'Waiting for the person\'s refresh of recent meetings to finish',
};

/**
 * What Admin → Queue says of a running row (ADR 0013): run by one of this process's slots; run by one of
 * them past its kind's lease (slow or hung — it keeps its slot rather than run twice); claimed by this
 * process but run by no slot (an orphan, claimed again after its lease); held by another process within
 * its lease; or held by another process past it (abandoned — the next claim takes it over).
 */
export const QUEUE_RUN_STATES = ['running', 'overran', 'orphaned', 'other_process', 'abandoned'] as const;
export type QueueRunState = (typeof QUEUE_RUN_STATES)[number];
/**
 * Why every word is Running, the most qualified: each row of the Running table is a job in 05-content §6's Running
 * state, and where it stands against its lease is a reading, not a state (§6.1, ruled A25 in design system 8.5.0) —
 * worded as a qualifier beside the state word, never as a word of its own in the State column, where a reader could
 * not tell one Running from a lease position. Each is one complete string (05-content §8).
 */
export const QUEUE_RUN_STATE_LABELS: Readonly<Record<QueueRunState, string>> = {
  running: 'Running',
  overran: 'Running — past its lease',
  orphaned: 'Running — orphaned',
  other_process: 'Running — in another process',
  abandoned: 'Running — abandoned by another process',
};

// --- To-dos ------------------------------------------------------------------------------------

export const TODO_STATUSES = ['open', 'done', 'dismissed'] as const;
export type TodoStatus = (typeof TODO_STATUSES)[number];
/** 05-content §6 (v8.1.0): awaiting its owner is Open, finished well is Completed, set aside is Dismissed. */
export const TODO_STATUS_LABELS: Readonly<Record<TodoStatus, string>> = {
  open: 'Open',
  done: 'Completed',
  dismissed: 'Dismissed',
};

export const TODO_PRIORITIES = ['high', 'medium', 'low'] as const;
export type TodoPriority = (typeof TODO_PRIORITIES)[number];
export const TODO_PRIORITY_LABELS: Readonly<Record<TodoPriority, string>> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};
/** Sort rank: lower sorts first in ascending priority order (high before low). */
export const TODO_PRIORITY_RANK: Readonly<Record<TodoPriority, number>> = {
  high: 0,
  medium: 1,
  low: 2,
};

/**
 * Where a to-do came from (ADR 0007; owner rulings A1/A6 of 2026-09-27 add the last two): `slack` is
 * an action item a Slack check of the meeting distilled, `assistant` one the cross-meeting brief did.
 */
export const TODO_SOURCES = ['calis_ai', 'fellow', 'manual', 'slack', 'assistant'] as const;
export type TodoSource = (typeof TODO_SOURCES)[number];
export const TODO_SOURCE_LABELS: Readonly<Record<TodoSource, string>> = {
  calis_ai: 'Calis AI',
  fellow: 'Fellow',
  manual: 'Added by you',
  slack: 'From Slack',
  assistant: 'Across meetings',
};

/**
 * Fellow's `completion_type` on an action item (migration 0018, `todos.fellow_completion`): `all` —
 * each assignee completes it; `any` — one completion closes it. Stored as Fellow sent it; linked to-dos
 * (ADR 0014, owner ruling Q4) never link an `all` item.
 */
export const FELLOW_COMPLETION_TYPES = ['all', 'any'] as const;
export type FellowCompletionType = (typeof FELLOW_COMPLETION_TYPES)[number];

/**
 * Who completed a to-do (migration 0019, `todos.completed_via`; ADR 0014): the person, on their own
 * row; Fellow, whose item arrived done; or a link — its state reached the copy. NULL while the
 * to-do is not done.
 */
export const TODO_COMPLETED_VIA = ['person', 'fellow', 'link'] as const;
export type TodoCompletedVia = (typeof TODO_COMPLETED_VIA)[number];

/**
 * The only sources a linked to-do may have (ADR 0014; design §3.4): made by a machine from the meeting
 * itself. `manual` is person-authored, and `slack` and `assistant` are distilled from one person's
 * private Slack or brief — none of them is ever linked. A daily sweep's meeting-less `slack` to-do
 * (`todos.from_sweep`, owner ruling Q5) has no meeting to link on either, and never links.
 */
export const TODO_LINKABLE_SOURCES = ['calis_ai', 'fellow'] as const satisfies readonly TodoSource[];

/**
 * A link's ONE stored state (migration 0019, `todo_links.state`; ADR 0014, S3): `none` until something sets it,
 * then the last change that did — open or done — with who set it and when. Nothing is derived from a history.
 */
export const TODO_LINK_STATES = ['none', 'open', 'done'] as const;
export type TodoLinkState = (typeof TODO_LINK_STATES)[number];

/**
 * A member's own last word said while a member (`todo_link_members.own_word`; ADR 0014, S3, S4): their own tick
 * (`done`) or un-tick (`open`). A change that reached the copy through the link is never a word.
 */
export const TODO_LINK_WORDS = ['open', 'done'] as const;
export type TodoLinkWord = (typeof TODO_LINK_WORDS)[number];

/**
 * Which rule set a link's state (`todo_links.state_set_by`; ADR 0014, invariant 8): a verified member's own change
 * (S4), a party joiner's done (S5), a waiting word promoted (S6), a merge of two links (S2), or — S4-F, Fellow sync
 * Stage 3 (ADR 0015; design §7.5) — Fellow's report of a change on an active party member's own copy, later than the
 * link's own time. Nothing else may.
 */
export const TODO_LINK_STATE_SET_BY = ['change', 'join', 'promotion', 'merge', 'fellow_report'] as const;
export type TodoLinkStateSetBy = (typeof TODO_LINK_STATE_SET_BY)[number];

/**
 * How a link formed (`todo_links.formed_via`) and how each member joined (`todo_link_members.joined_via`): the
 * same Fellow action item, or Calis's AI matching. A record only — whether a member is a party is decided from the
 * copies (ADR 0014, "Terms"), never from this.
 */
export const TODO_LINK_FORMED_VIA = ['fellow_item', 'matcher'] as const;
export type TodoLinkFormedVia = (typeof TODO_LINK_FORMED_VIA)[number];

/** What a change through a link did to a copy (`todos.via_link_action`, since migration 0027). */
export const TODO_LINK_VIA_ACTIONS = ['done', 'reopen'] as const;
export type TodoLinkViaAction = (typeof TODO_LINK_VIA_ACTIONS)[number];

/** Whose change it was (`todos.via_link_source`, `todo_links.state_by_source`): a person's, or Fellow's report. */
export const TODO_LINK_VIA_SOURCES = ['person', 'fellow'] as const;
export type TodoLinkViaSource = (typeof TODO_LINK_VIA_SOURCES)[number];

/**
 * Why Calis took a to-do out of its link without its owner (the `todo.unlink` audit row's `reason`):
 * its copy of the meeting is no longer the one it joined through, its source is no longer one that
 * links, its owner opted the item out, its own copy of the meeting no longer lists its owner as
 * invited, or its owner's own Fellow copy of the link's Fellow action item took its place on the link
 * (`displaced`: a link holds one to-do of a person, and a party's copy of the item is the one) — or the to-do itself is
 * about to be deleted (`deleted`: its owner's delete, a regeneration replacing it, or its account's deletion, each of
 * which ends the membership by cascade; the owner's ruling PM-9 of 2026-10-10, "A row per ended share").
 */
export const TODO_UNLINK_REASONS = ['meeting_changed', 'source_ineligible', 'opted_out', 'not_invited', 'displaced', 'deleted'] as const;
export type TodoUnlinkReason = (typeof TODO_UNLINK_REASONS)[number];

/**
 * How the last AI look at a shared meeting ended (`todo_link_scans.outcome`, migration 0020; ADR 0014,
 * ruling Q2(a)): links formed; nothing the AI called certain; nobody who holds AI summaries to compare
 * (the Fellow item step alone ran); the day's cap of looks at this meeting reached; or the call failed.
 */
export const TODO_LINK_SCAN_OUTCOMES = ['linked', 'nothing', 'skipped_ungated', 'capped', 'failed'] as const;
export type TodoLinkScanOutcome = (typeof TODO_LINK_SCAN_OUTCOMES)[number];

/** The matcher's word on a group of to-dos (ruling Q4(a)): one piece of work any of them finishes, or work each owes. */
export const TODO_LINK_MATCH_COMPLETIONS = ['shared', 'each'] as const;
export type TodoLinkMatchCompletion = (typeof TODO_LINK_MATCH_COMPLETIONS)[number];

/** How sure the matcher is; only `certain` ever forms a link. */
export const TODO_LINK_MATCH_CONFIDENCES = ['certain', 'likely'] as const;
export type TodoLinkMatchConfidence = (typeof TODO_LINK_MATCH_CONFIDENCES)[number];

/**
 * How sure Calis's look at one person's Fellow items is that an item is the same work as one of their Calis AI to-dos
 * (the `record_fellow_matches` tool, ADR 0015 Stage 2). Only `certain` ever ties: a tie ticks the item off in Fellow
 * under the person's name, with no confirmation (owner decision O2(b)).
 */
export const FELLOW_MATCH_CERTAINTIES = ['certain', 'likely'] as const;
export type FellowMatchCertainty = (typeof FELLOW_MATCH_CERTAINTIES)[number];

// --- Fellow action items a person holds (migration 0028, `todo_fellow_items`; owner, 2026-09-30) ----------
// Why every list is complete now, later stages' values included: SQLite cannot alter a CHECK, so the table is
// made once with the full lists and a later stage only adds columns. A value a stage does not use yet is never
// written by it.

/**
 * What became of a Fellow item the person holds (`todo_fellow_items.resolution`): `imported` as a `fellow`
 * to-do; `tied` to one of their own Calis to-dos (Stage 2); `pending`, held briefly while Calis decides
 * (Stage 2); `dropped`, the person deleted its to-do — the item is never imported again.
 */
export const FELLOW_ITEM_RESOLUTIONS = ['pending', 'imported', 'tied', 'dropped'] as const;
export type FellowItemResolution = (typeof FELLOW_ITEM_RESOLUTIONS)[number];

/** How a tie was made (`todo_fellow_items.matched_by`, Stage 2): the same wording, or Calis's AI. */
export const FELLOW_ITEM_MATCHED_BY = ['wording', 'ai'] as const;
export type FellowItemMatchedBy = (typeof FELLOW_ITEM_MATCHED_BY)[number];

/**
 * What a `pending` holding waits for (`todo_fellow_items.pending_reason`, migration 0034, Stage 2 "Import or tie"):
 * `summary`, the person's own summary of the meeting, whose to-dos the item may be the same as; `match`, Calis's own
 * look at the person's to-dos for it. Why held at all: an item imported before the to-do it matches exists would be a
 * second to-do of the same work — the owner: "imported only if they don't match an existing to-do generated by calis".
 */
export const FELLOW_ITEM_PENDING_REASONS = ['summary', 'match'] as const;
export type FellowItemPendingReason = (typeof FELLOW_ITEM_PENDING_REASONS)[number];

/**
 * Why a Fellow to-do was kept beside the person's Calis AI to-do of the same work instead of folded into it
 * (`todo_fellow_items.kept_reason`, migration 0036; owner decision O8(c), "Dismiss Fellow copy": "The untouched Fellow copy
 * is dismissed and its item is tied to the AI to-do instead"). Only an untouched copy is dismissed; one the person — or a
 * colleague, through a link — has acted on is kept, and this says what made it touched, in the order Calis asks:
 * `written`, Calis owes or sent Fellow a change for it; `linked`, it is on a link with colleagues' copies, or a change
 * reached it through one; `noted`, it carries a note; `done`, it is done; `edited`, the person changed it (or dismissed it).
 */
export const FELLOW_ITEM_KEPT_REASONS = ['written', 'linked', 'noted', 'done', 'edited'] as const;
export type FellowItemKeptReason = (typeof FELLOW_ITEM_KEPT_REASONS)[number];
export const FELLOW_ITEM_KEPT_REASON_LABELS: Readonly<Record<FellowItemKeptReason, string>> = {
  written: 'Calis had a change for it in Fellow',
  linked: "It is linked with colleagues' to-dos",
  noted: 'It has a note',
  done: 'It is done',
  edited: 'You changed it',
};

/**
 * Open or done, as Calis wants Fellow to show an item (`want`), last wrote it (`sent`), or last compared its
 * to-do with Fellow (`reconciled_status`) — `todo_fellow_items`, Stage 1.
 */
export const FELLOW_ITEM_WANTS = ['open', 'done'] as const;
export type FellowItemWant = (typeof FELLOW_ITEM_WANTS)[number];

/**
 * What Fellow last said of an item (`todo_fellow_items.fellow_state`): open, done, archived, or `per_person` —
 * an "everyone completes" item, whose state is each assignee's own and which Calis never writes.
 */
export const FELLOW_ITEM_STATES = ['open', 'done', 'archived', 'per_person'] as const;
export type FellowItemState = (typeof FELLOW_ITEM_STATES)[number];
export const FELLOW_ITEM_STATE_LABELS: Readonly<Record<FellowItemState, string>> = {
  open: 'Open in Fellow',
  // Why Completed (05-content §6: "Done" is on Completed's Not-list; the owner's ruling Q9 of escalation
  // 20261005-200526, "Completed in Fellow" everywhere; Phase 1c R14): the app's own word for the state, said of Fellow.
  done: 'Completed in Fellow',
  archived: 'Archived in Fellow',
  per_person: 'Everyone completes their own part in Fellow',
};

/**
 * The one-click remedy a row offers when Calis and Fellow disagree and nothing was written
 * (`todo_fellow_items.offer`): change Fellow (Stage 1), or change the to-do here (Stage 3).
 */
export const FELLOW_ITEM_OFFERS = ['tick_fellow', 'reopen_fellow', 'tick_here', 'reopen_here'] as const;
export type FellowItemOffer = (typeof FELLOW_ITEM_OFFERS)[number];
export const FELLOW_ITEM_OFFER_LABELS: Readonly<Record<FellowItemOffer, string>> = {
  tick_fellow: 'Tick off in Fellow',
  reopen_fellow: 'Reopen in Fellow',
  tick_here: 'Tick off here',
  reopen_here: 'Reopen here',
};

/**
 * Why Calis did not, or could not, tick an item off (or reopen it) in Fellow (`todo_fellow_items.write_error`,
 * Stage 1). Each is a `not_possible` — Fellow's item is not one Calis may or can write, said once and muted —
 * or a `failure` — the write was refused or did not get through, shown as a warning with "Try again".
 */
export const FELLOW_WRITE_CODES = [
  'no_target',
  'unverifiable',
  'other_workspace',
  'not_found',
  'archived',
  'per_person',
  'assigned_to_others',
  'forbidden',
  'api_off',
  'refused',
  'auth_failed',
  'unavailable',
] as const;
export type FellowWriteCode = (typeof FELLOW_WRITE_CODES)[number];
export const FELLOW_WRITE_CODE_LABELS: Readonly<Record<FellowWriteCode, string>> = {
  no_target: "Fellow gave this item no id, so Calis can't tick it off there.",
  unverifiable: "Calis can't confirm this is the item in Fellow, so it didn't tick it off there.",
  other_workspace: 'This meeting is from another Fellow workspace than the one you connected.',
  not_found: 'Fellow no longer has this item.',
  archived: 'Archived in Fellow',
  per_person: 'Everyone completes their own part in Fellow',
  assigned_to_others: 'Assigned to others in Fellow; not ticked off there',
  forbidden: "Fellow doesn't let you tick this one off.",
  api_off: "Fellow's API is turned off for your account.",
  refused: 'Fellow refused the change.',
  auth_failed: "Fellow isn't connected. Reconnect.",
  unavailable: "Couldn't tick off in Fellow.",
};
export const FELLOW_WRITE_CODE_KINDS = ['not_possible', 'failure'] as const;
export type FellowWriteCodeKind = (typeof FELLOW_WRITE_CODE_KINDS)[number];
/** Why the split: a `failure` is worth trying again and is listed under "Not ticked off in Fellow"; a `not_possible` is not. */
export const FELLOW_WRITE_CODE_KIND: Readonly<Record<FellowWriteCode, FellowWriteCodeKind>> = {
  no_target: 'not_possible',
  unverifiable: 'not_possible',
  other_workspace: 'not_possible',
  not_found: 'not_possible',
  archived: 'not_possible',
  per_person: 'not_possible',
  assigned_to_others: 'not_possible',
  forbidden: 'failure',
  api_off: 'failure',
  refused: 'failure',
  auth_failed: 'failure',
  unavailable: 'failure',
};

/**
 * Why Calis's last read of the person's Fellow action-item list did not go through (`fellow_connections.items_error_code`,
 * migration 0042; ADR 0015 Stage 3, owner decision O4(a): "Both come back within about 15 minutes"). The read is a step of
 * the person's own tick-off run, isolated from imports (design §7.2): its failure is kept here and never changes the
 * connection's status (a key Fellow no longer accepts excepted, which breaks the connection as any call's would), so a
 * list Fellow refuses never stops meetings coming in. `forbidden`, Fellow answered 403; `not_offered`, Fellow has no list
 * for this key (404 or a redirect); `refused`, Fellow refused the request or answered what Calis cannot read;
 * `unavailable`, Fellow could not be reached — tried again at the next sync.
 */
export const FELLOW_STATE_READ_CODES = ['forbidden', 'not_offered', 'refused', 'unavailable'] as const;
export type FellowStateReadCode = (typeof FELLOW_STATE_READ_CODES)[number];
export const FELLOW_STATE_READ_CODE_LABELS: Readonly<Record<FellowStateReadCode, string>> = {
  forbidden: "Fellow didn't let Calis read your action items, so changes made in Fellow don't reach Calis.",
  not_offered: "Fellow doesn't offer your action-item list to your key, so changes made in Fellow don't reach Calis.",
  refused: "Fellow refused Calis's read of your action items.",
  unavailable: "Couldn't read your action items in Fellow; Calis tries again at the next sync.",
};

/**
 * A note Calis attached under a to-do (ruling A3): what a meeting's Slack check, the brief, or the daily Slack sweep
 * (`sweep`, owner ruling Q5 of 2026-09-30, migration 0039) shows about it — or (`fellow`, migration 0036, owner decision
 * O8(c)) why Calis dismissed a Fellow to-do: its Fellow item is now tied to the person's own Calis AI to-do of the same
 * work, which ticks it off in Fellow.
 */
export const TODO_NOTE_KINDS = ['slack', 'assistant', 'fellow', 'sweep'] as const;
export type TodoNoteKind = (typeof TODO_NOTE_KINDS)[number];
export const TODO_NOTE_KIND_LABELS: Readonly<Record<TodoNoteKind, string>> = {
  slack: 'From Slack',
  // Why "From the Assistant" and not the source chip's "Across meetings": a note under a to-do says who
  // wrote it — the brief judged this to-do settled or moot (owner ruling A13) — as "From Slack" does.
  assistant: 'From the Assistant',
  // Why "In Fellow": the note says where the dismissed to-do's item now lives — tied to the to-do it names, in Fellow.
  fellow: 'In Fellow',
  // Why a kind of its own under the same words: the person reads both as what Slack said, but a meeting's check
  // replaces every `slack` note on its meeting's to-dos, and the sweep reads the person's whole day — each replaces
  // only its own kind (the sweep's, owner-wide, at its next analysed look), so neither wipes the other's notes.
  sweep: 'From Slack',
};

// --- Meetings, summaries, connections ----------------------------------------------------------

export const TRANSCRIPT_STATUSES = ['available', 'empty'] as const;
export type TranscriptStatus = (typeof TRANSCRIPT_STATUSES)[number];
export const TRANSCRIPT_STATUS_LABELS: Readonly<Record<TranscriptStatus, string>> = {
  available: 'Transcript available',
  empty: 'No transcript',
};

export const SUMMARY_PROVIDERS = ['anthropic', 'offline'] as const;
export type SummaryProviderId = (typeof SUMMARY_PROVIDERS)[number];
export const SUMMARY_PROVIDER_LABELS: Readonly<Record<SummaryProviderId, string>> = {
  anthropic: 'Anthropic Claude',
  offline: 'Demo summariser',
};

// Why no label map: a summary's state reaches the reader only through `summaryPresentation` (pages/lib/summary.ts),
// in 05-content §6's words; the Queued/Generating/Ready map nothing read was deleted at the conformity audit of
// 2026-10-05 (R5) rather than left to be quoted.
export const SUMMARY_STATUSES = ['queued', 'running', 'ready', 'failed'] as const;
export type SummaryStatus = (typeof SUMMARY_STATUSES)[number];

// Why no label map: the Connections page states each system's State as its most recent run's word
// (`lastRunStatePresentation`, 06-modules §6.4), and words a Slack connection's own state itself
// (`slackStatusPresentation`: Active, Running, Failed, Revoked) in 05-content §6's vocabulary; the
// Connected/Disconnected map nothing read was deleted at the conformity audit of 2026-10-05 (pick C) rather than left
// to be quoted.
export const CONNECTION_STATUSES = ['active', 'error', 'revoked'] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

// --- Slack -------------------------------------------------------------------------------------

/**
 * The kinds of Slack conversation Calis knows. By default a check may read every one the person can
 * see (owner ruling 20260926-002133, amended, R7); the person may leave channels out one by one, and
 * direct messages (`im`) and group DMs (`mpim`) out all together. `SLACK_CONVERSATION_TYPES` configures
 * which are asked for.
 */
export const SLACK_CONVERSATION_KINDS = ['public_channel', 'private_channel', 'im', 'mpim'] as const;
export type SlackConversationKind = (typeof SLACK_CONVERSATION_KINDS)[number];
export const SLACK_CONVERSATION_KIND_LABELS: Readonly<Record<SlackConversationKind, string>> = {
  public_channel: 'Public channel',
  private_channel: 'Private channel',
  im: 'Direct message',
  mpim: 'Group DM',
};
/** The kinds a person may leave out one by one; direct messages and group DMs go with the DM switch. */
export const SLACK_CHANNEL_KINDS = ['public_channel', 'private_channel'] as const satisfies readonly SlackConversationKind[];
export type SlackChannelKind = (typeof SLACK_CHANNEL_KINDS)[number];

/**
 * Where the Slack connect flow ends: `/connections?slack=<outcome>`. The labels are the page's copy.
 * `excess_scope` is gone (owner ruling of 2026-09-27: a grant wider than Calis asks for is kept); an old
 * address still carrying it is an unknown value, which the page drops without a word.
 */
export const SLACK_CONNECT_OUTCOMES = [
  'connected',
  'cancelled',
  'expired',
  'not_configured',
  'not_permitted',
  'wrong_account',
  'wrong_workspace',
  'enterprise_install',
  'account_in_use',
  'guest_account',
  'missing_scope',
  'error',
] as const;
export type SlackConnectOutcome = (typeof SLACK_CONNECT_OUTCOMES)[number];
export const SLACK_CONNECT_OUTCOME_LABELS: Readonly<Record<SlackConnectOutcome, string>> = {
  connected: 'Slack is connected.',
  cancelled: 'You cancelled connecting Slack. Nothing was changed.',
  expired: 'Connecting Slack took too long. Please try again.',
  not_configured: 'Slack is not switched on in this installation.',
  not_permitted: 'Your access doesn\'t include Slack connect. Ask an administrator to add you to a group with Slack connect.',
  wrong_account: 'That Slack account uses a different email address from your Calis account. Connect with the Slack account that uses the same address.',
  wrong_workspace: 'Calis cannot connect to that Slack workspace. Connect with your Centro Slack workspace.',
  enterprise_install: 'Calis connects to one Slack workspace, not to a whole Enterprise organisation. Connect again and choose a workspace.',
  account_in_use: 'That Slack account is already connected to another Calis account.',
  guest_account: 'Guest Slack accounts cannot be connected.',
  missing_scope: 'Slack did not grant all the access Calis asks for. Connect again and allow each item Slack lists.',
  error: 'Slack could not be connected. Please try again.',
};

/** The life of one Slack cross-check of a meeting (`meeting_slack_analyses.status`). */
export const SLACK_CONTEXT_STATUSES = ['queued', 'running', 'ready', 'failed'] as const;
export type SlackContextStatus = (typeof SLACK_CONTEXT_STATUSES)[number];

/** What a running Slack cross-check is doing: reading Slack, then comparing it with the meeting. */
export const SLACK_CONTEXT_PHASES = ['reading', 'comparing'] as const;
export type SlackContextPhase = (typeof SLACK_CONTEXT_PHASES)[number];
/** The meeting's Slack tab while a check runs. */
export const SLACK_CONTEXT_PHASE_LABELS: Readonly<Record<SlackContextPhase, string>> = {
  reading: 'Reading Slack',
  comparing: 'Comparing with the meeting',
};

/** Why a Slack cross-check ran: after a new summary, on request, or as the one later re-check. */
export const SLACK_CONTEXT_TRIGGERS = ['summary', 'manual', 'follow_up'] as const;
export type SlackContextTrigger = (typeof SLACK_CONTEXT_TRIGGERS)[number];

/** Why a check read less than it was asked to; the result is then shown as partial, never complete. */
export const SLACK_PARTIAL_REASONS = ['call_budget', 'time_budget', 'rate_limited', 'source_unreadable', 'message_cap'] as const;
export type SlackPartialReason = (typeof SLACK_PARTIAL_REASONS)[number];
/** What the meeting's Slack tab says above a partial result: what was not read, and why. */
export const SLACK_PARTIAL_REASON_LABELS: Readonly<Record<SlackPartialReason, string>> = {
  call_budget: 'Calis stopped reading early to stay within the number of Slack requests one check may make.',
  time_budget: 'Calis stopped reading early because the check ran out of time.',
  rate_limited: 'Slack asked Calis to slow down, so it stopped reading early.',
  source_unreadable: 'Some conversations could not be read — you may have left a channel, or it was archived.',
  message_cap: 'There were more messages than one check reads, so Calis read as many as it may and stopped there.',
};

/**
 * The daily Slack sweep's deployment switch (SLACK_SWEEP_MODE; owner request of 2026-09-30, rulings Q1–Q8 and
 * N1–N4). `off` reads nothing. `measure` is the reader running dark: it reads each person's day as the sweep
 * will, keeps counts only and sends nothing anywhere — so whether Slack's search by day alone works (N1), and
 * what a day costs in calls and time, can be known before any AI step builds on it. `on` also sends what concerns
 * the person to the AI provider (`record_slack_sweep`) and seals what it found; it is the default since 2026-10-01
 * (the owner: "Why is it switched off. I want it on"), set before any measurement was reviewed — `off` is a
 * deployment's kill switch and `measure` its way back to counts only.
 */
export const SLACK_SWEEP_MODES = ['off', 'measure', 'on'] as const;
export type SlackSweepMode = (typeof SLACK_SWEEP_MODES)[number];

/** The life of one daily sweep (`slack_sweeps.status`): its row is made when it starts, so it is never queued. */
export const SLACK_SWEEP_STATUSES = ['running', 'ready', 'failed'] as const;
export type SlackSweepStatus = (typeof SLACK_SWEEP_STATUSES)[number];

/** What a sweep did (`slack_sweeps.mode`): counted what it read (`measure`), or also had it analysed (`analyse`, a later step). */
export const SLACK_SWEEP_ROW_MODES = ['measure', 'analyse'] as const;
export type SlackSweepRowMode = (typeof SLACK_SWEEP_ROW_MODES)[number];

/**
 * How a sweep reached the public channels the person never joined (owner ruling N1: every public channel of the
 * company, through Slack's search by day alone, which Slack does not document). Anything but `read` makes the
 * sweep partial, and it says why — it never passes the joined channels off as the whole company.
 */
export const SLACK_SWEEP_PUBLIC_SEARCH = ['read', 'partial', 'unavailable', 'not_granted'] as const;
export type SlackSweepPublicSearch = (typeof SLACK_SWEEP_PUBLIC_SEARCH)[number];
export const SLACK_SWEEP_PUBLIC_SEARCH_LABELS: Readonly<Record<SlackSweepPublicSearch, string>> = {
  read: 'Calis searched the day across every public channel, including those you have not joined.',
  partial: 'Calis could search only part of the day across public channels, so some messages in channels you have not joined were not read.',
  unavailable: 'Slack would not list the day\'s messages across public channels, so channels you have not joined were not read.',
  not_granted: 'Calis may not search Slack with your connection, so channels you have not joined were not read.',
};

/**
 * What the daily sweep says Slack shows of one of the brief's open items (`open_item_updates`, owner request of
 * 2026-09-30: "Right now it might detect ongoing events but never update them"): it moved on, it is settled, it is
 * stuck, a later decision replaced it, or it is still going with nothing new. `resolved` and `overtaken` need
 * cited evidence (as ruling A13's verdicts do).
 */
export const SLACK_SWEEP_UPDATE_STATUSES = ['progressed', 'resolved', 'blocked', 'overtaken', 'unchanged_but_active'] as const;
export type SlackSweepUpdateStatus = (typeof SLACK_SWEEP_UPDATE_STATUSES)[number];
/**
 * A "Mark done" offer under one of the person's to-dos (owner ruling Q8(b) of 2026-09-30, "One-click offer": "Also
 * offer a one-click \"Mark done\" on the to-do"; migration 0040): `open` while it may be shown and accepted; `accepted`
 * when the person clicked Mark done (their own tick did the rest); `dismissed` when they clicked Not done; `closed`
 * when it lapsed without either — expired, superseded by a newer offer on the same to-do, or the to-do changed since.
 * Only an open, live offer ever ticks anything, and only through the person's own click.
 */
export const TODO_DONE_OFFER_STATUSES = ['open', 'accepted', 'dismissed', 'closed'] as const;
export type TodoDoneOfferStatus = (typeof TODO_DONE_OFFER_STATUSES)[number];
/**
 * What made an offer: the daily Slack sweep. `slack_check` is reserved for a meeting's Slack check offering one later
 * (design §4, "Later"); nothing writes it yet — the list is complete once, as 0028's, so the table is never rebuilt
 * for it.
 */
export const TODO_DONE_OFFER_ORIGINS = ['slack_sweep', 'slack_check'] as const;
export type TodoDoneOfferOrigin = (typeof TODO_DONE_OFFER_ORIGINS)[number];
/**
 * Why none is a 05-content §6 word: each is the AI's reading of what Slack shows of an item, not the item's state — a
 * verdict, which §6.1 (ruled A25 in design system 8.5.0) words as a reading and never puts in one column with §6's words,
 * Resolved and Blocked included (they were here until that ruling; conformity 2026-10-05, R5 and S4).
 */
export const SLACK_SWEEP_UPDATE_STATUS_LABELS: Readonly<Record<SlackSweepUpdateStatus, string>> = {
  progressed: 'Moved on',
  resolved: 'Settled',
  blocked: 'Obstacle in the way',
  overtaken: 'Overtaken',
  unchanged_but_active: 'Still going',
};

/**
 * Why part of a daily sweep's answer was not kept (ai/slack-sweep/validate.ts): counts only, on the sweep's audit
 * row, so an administrator can tell a thin answer from a refused one. A new matter citing a message never sent,
 * or a meeting never shown; text that links, addresses or identifies, or is copied from a message; a repeat of a
 * matter already open; an update or verdict on an id never shown, without its words, or settling a matter
 * without citing the messages that show it. Version 2 of the sweep's answer (owner ruling Q5) adds its "From Slack"
 * to-dos (`item_*`: empty or not verb first, resting on no kept matter or on one of low confidence, text that links,
 * names a conversation or copies, a repeat of a to-do shown, past five), its notes under the person's to-dos
 * (`note_*`: on a to-do never shown, citing a message never sent, empty, unsafe, copied, a second on one to-do, past
 * ten) and verdicts on its own earlier to-dos naming one never shown (`review_unknown_todo`). Version 3 (owner ruling
 * Q8(b)) adds its signs that one of the person's to-dos is done (`done_*`: on a to-do never shown, citing a message
 * never sent or none, of low confidence, resting on messages posted before the to-do was made, empty, unsafe, copied,
 * a second on one to-do, past ten, or on a to-do Calis already suggested is done).
 */
export const SLACK_SWEEP_DROP_REASONS = [
  'invented_citation',
  'invented_item',
  'empty',
  'unsafe_text',
  'verbatim',
  'duplicate',
  'over_limit',
  'update_unknown_ref',
  'update_invented_citation',
  'update_no_evidence',
  'update_empty',
  'update_unsafe_text',
  'update_verbatim',
  'update_duplicate',
  'review_unknown_ref',
  'review_invented_citation',
  'review_no_evidence',
  'review_empty',
  'review_unsafe_text',
  'review_verbatim',
  'review_duplicate',
  'item_empty',
  'item_unknown_matter',
  'item_low_confidence',
  'item_unsafe_text',
  'item_verbatim',
  'item_duplicate',
  'item_over_limit',
  'note_unknown_todo',
  'note_invented_citation',
  'note_empty',
  'note_unsafe_text',
  'note_verbatim',
  'note_duplicate',
  'note_over_limit',
  'review_unknown_todo',
  'done_unknown_todo',
  'done_invented_citation',
  'done_no_evidence',
  'done_low_confidence',
  'done_stale_evidence',
  'done_empty',
  'done_unsafe_text',
  'done_verbatim',
  'done_duplicate',
  'done_over_limit',
  'done_already_offered',
] as const;
export type SlackSweepDropReason = (typeof SLACK_SWEEP_DROP_REASONS)[number];

export const SLACK_FINDING_KINDS = ['context', 'inconsistency', 'opportunity'] as const;
export type SlackFindingKind = (typeof SLACK_FINDING_KINDS)[number];
/** Group headings on the meeting's Slack tab. */
export const SLACK_FINDING_KIND_LABELS: Readonly<Record<SlackFindingKind, string>> = {
  context: 'Additional context',
  inconsistency: 'Inconsistencies',
  opportunity: 'Opportunities',
};
/** One finding's kind, on its own pill. */
export const SLACK_FINDING_KIND_NAMES: Readonly<Record<SlackFindingKind, string>> = {
  context: 'Context',
  inconsistency: 'Inconsistency',
  opportunity: 'Opportunity',
};

export const SLACK_OPPORTUNITY_TYPES = ['follow_up', 'unowned_ask', 'loop_in', 'decision_pending'] as const;
export type SlackOpportunityType = (typeof SLACK_OPPORTUNITY_TYPES)[number];
export const SLACK_OPPORTUNITY_TYPE_LABELS: Readonly<Record<SlackOpportunityType, string>> = {
  follow_up: 'Follow up',
  unowned_ask: 'Nobody owns this',
  loop_in: 'Loop someone in',
  decision_pending: 'Waiting on a decision',
};

export const SLACK_FINDING_CONFIDENCE = ['high', 'medium', 'low'] as const;
export type SlackFindingConfidence = (typeof SLACK_FINDING_CONFIDENCE)[number];

export const SLACK_FINDING_IMPORTANCE = ['high', 'medium', 'low'] as const;
export type SlackFindingImportance = (typeof SLACK_FINDING_IMPORTANCE)[number];
export const SLACK_FINDING_IMPORTANCE_LABELS: Readonly<Record<SlackFindingImportance, string>> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * Why a finding the AI provider returned was not kept (validate.ts). Counted in the analysis audit row,
 * never shown one by one. `invented_citation` and `invented_item` are also the run-level signal: when
 * they are most of the answer, the whole answer is refused.
 */
export const SLACK_FINDING_DROP_REASONS = [
  'invented_citation',
  'invented_item',
  'one_sided',
  'untyped_opportunity',
  'bad_loop_in',
  'empty',
  'unsafe_text',
  'verbatim',
  'duplicate',
  'over_limit',
  // Why a distilled action item or a to-do note was not kept (validate.ts, owner rulings A1–A2). Counted
  // apart from the findings' reasons so a check's audit row tells which part of the answer was thin.
  'item_empty',
  'item_unsafe_text',
  'item_verbatim',
  'item_low_confidence',
  'item_unknown_finding',
  'item_unknown_todo',
  'item_duplicate',
  'item_over_limit',
  // Why a verdict on the previous check's conclusions was not kept (validate.ts, owner ruling A13):
  // an id never shown, a verdict that invalidates a conclusion without a finding of this check as
  // evidence, an empty, unsafe or copied reason, or a second verdict on one id. `review_superseded`
  // counts a finding of this check dropped because it repeats a prior finding the same answer judged
  // resolved.
  'review_unknown_ref',
  'review_no_evidence',
  'review_empty',
  'review_unsafe_text',
  'review_verbatim',
  'review_duplicate',
  'review_superseded',
] as const;
export type SlackFindingDropReason = (typeof SLACK_FINDING_DROP_REASONS)[number];

/**
 * What a re-check says of one of the previous check's conclusions (owner ruling A13, 2026-09-28): a
 * finding, a to-do it made or a note it wrote. Closed: the tool schema lists it and validate.ts
 * checks it; a verdict other than `still_holds` or `unclear` needs evidence from the new check.
 */
export const SLACK_PRIOR_VERDICTS = ['still_holds', 'resolved', 'overtaken', 'no_longer_relevant', 'unclear'] as const;
export type SlackPriorVerdict = (typeof SLACK_PRIOR_VERDICTS)[number];
/**
 * A verdict is a reading, not a state (05-content §6.1, ruled A25 in design system 8.5.0: "what a model judged of an
 * earlier message — still holds, overtaken by later discussion"), so none is a §6 word: `resolved` reads "Settled by
 * later discussion", naming what was read as its neighbour does, never §6's Resolved (conformity 2026-10-05, R5 and S4).
 */
export const SLACK_PRIOR_VERDICT_LABELS: Readonly<Record<SlackPriorVerdict, string>> = {
  still_holds: 'Still holds',
  resolved: 'Settled by later discussion',
  overtaken: 'Overtaken by later discussion',
  no_longer_relevant: 'No longer relevant',
  unclear: 'Unclear',
};

/**
 * Why an entry of the assistant's brief, or one of its distilled action items, was not kept
 * (ai/assistant/validate.ts). Counted in the brief's audit row, never shown one by one. `unknown_ref`
 * is also the run-level signal: when invented ids are most of the answer, the whole answer is refused.
 */
export const ASSISTANT_DROP_REASONS = [
  'unknown_ref',
  'one_sided',
  'unknown_meeting',
  'low_confidence',
  'empty',
  'unsafe_text',
  'duplicate',
  'over_limit',
  // Verdicts on the previous brief (owner ruling A13): appended, never reordered.
  'review_unknown_ref',
  'review_no_evidence',
  'review_empty',
  'review_unsafe_text',
  'review_duplicate',
  /** An entry of this brief repeating one of the previous brief's that it judged settled: not shown again. */
  'review_superseded',
] as const;
export type AssistantDropReason = (typeof ASSISTANT_DROP_REASONS)[number];

/** What the owner said about a finding; it survives re-checks by fingerprint. */
export const SLACK_FEEDBACK_VERDICTS = ['dismissed'] as const;
export type SlackFeedbackVerdict = (typeof SLACK_FEEDBACK_VERDICTS)[number];

// --- The assistant (owner rulings A1–A9 and A11 of 2026-09-27, escalation 20260927-162056) -------

/** The life of one brief — the per-person overview across recent meetings (`assistant_briefs.status`). */
// Why no label map: the Assistant page words a brief's job itself (`refreshNowOffer`, pages/lib/assistant.ts — Running,
// Pending) in 05-content §6's words; the Pending/Updating/Ready map nothing read was deleted at the conformity audit of
// 2026-10-05 (R5) rather than left to be quoted.
export const ASSISTANT_BRIEF_STATUSES = ['queued', 'running', 'ready', 'failed'] as const;
export type AssistantBriefStatus = (typeof ASSISTANT_BRIEF_STATUSES)[number];

/**
 * Why a brief ran: because new material landed (a summary or a Slack check), because the person asked (A7), or
 * after the person's daily Slack sweep, quiet or not (owner request of 2026-09-30, ruling Q4: "one fresh brief per
 * person after each daily scan, so dates, overdue items and open matters stay current").
 */
export const ASSISTANT_TRIGGERS = ['auto', 'manual', 'sweep'] as const;
export type AssistantTrigger = (typeof ASSISTANT_TRIGGERS)[number];
export const ASSISTANT_TRIGGER_LABELS: Readonly<Record<AssistantTrigger, string>> = {
  auto: 'After new material',
  manual: 'On request',
  sweep: 'After the daily Slack sweep',
};

/**
 * What running work a queued brief waits for before it can start, beside the jobs that start first (owner
 * ruling 4 of 2026-09-28, ADR 0013): the person's own job in progress (one job per person at a time), the
 * AI service's cool-down after it asked Calis to wait (no AI job starts meanwhile), or every worker slot
 * that could take it busy. The label completes "waiting for …" on the pending line.
 */
export const ASSISTANT_BRIEF_WAITS = ['your_job', 'ai_service', 'free_slot'] as const;
export type AssistantBriefWait = (typeof ASSISTANT_BRIEF_WAITS)[number];
export const ASSISTANT_BRIEF_WAIT_LABELS: Readonly<Record<AssistantBriefWait, string>> = {
  your_job: 'your job in progress',
  ai_service: 'the AI service',
  free_slot: 'a free slot',
};

/** How an ongoing matter across meetings stands, as the brief's tool reports it and the page's pill shows it. */
export const ASSISTANT_THREAD_STATUSES = ['on_track', 'at_risk', 'blocked', 'waiting', 'done', 'unclear'] as const;
export type AssistantThreadStatus = (typeof ASSISTANT_THREAD_STATUSES)[number];
/**
 * Why none is a 05-content §6 word: the health of a matter is the AI's reading, not the matter's state (§6.1, ruled A25
 * in design system 8.5.0: "the health of a matter — on track, at risk"), so its pill is worded as a reading and never
 * shares a column with §6's words — Blocked, Awaiting someone else and Completed, which stood here until that ruling,
 * included (conformity 2026-10-05, R5 and S4; Phase 1's R2). The brief's own meaning of `waiting` is "waiting (on
 * someone else)" (ai/assistant/prompt.ts); the thread's summary and next step say who. The daily sweep's prompt reads
 * these words lowercased (ai/slack-sweep/prompt.ts), so each stays a plain phrase.
 */
export const ASSISTANT_THREAD_STATUS_LABELS: Readonly<Record<AssistantThreadStatus, string>> = {
  on_track: 'On track',
  at_risk: 'At risk',
  blocked: 'Obstacle in the way',
  waiting: 'Up to someone else',
  done: 'Wrapped up',
  unclear: 'Unclear',
};

/** Of two sides of a cross-meeting inconsistency, which was said later — or neither can be told. */
export const ASSISTANT_LATER_SIDES = ['a', 'b', 'unclear'] as const;
export type AssistantLaterSide = (typeof ASSISTANT_LATER_SIDES)[number];
export const ASSISTANT_LATER_SIDE_LABELS: Readonly<Record<AssistantLaterSide, string>> = {
  a: 'The first was said later',
  b: 'The second was said later',
  unclear: 'Cannot tell which came later',
};

// --- AI call ledger (owner ruling of 2026-09-27: every AI call, with its cost, for administrators) ---

/** What an AI call was for. Every call Calis makes is one of these; the ledger holds one row per call. */
// Why `fellow_match` (migration 0035): Calis's look at one person's Fellow items against their own AI to-dos — their
// material, so the row names its owner and its meeting, unlike `todo_link`. Why `project_sort` (migration 0052; owner
// ruling 11 of 2026-10-04, ADR 0018): the call that sorts one person's open to-dos that are in no project — their
// material, so the row names its owner and its job; it points at no record (no target, no meeting): it reads many. Why
// `executive_report` (migration 0053; ADR 0019; design §2.9): the executives' organisation report — made for nobody, so
// its row names no person (the `todo_link` precedent) and points at the report it wrote.
export const AI_CALL_KINDS = ['summary', 'slack_judge', 'slack_analysis', 'assistant_brief', 'todo_link', 'slack_sweep', 'fellow_match', 'project_sort', 'executive_report'] as const;
export type AiCallKind = (typeof AI_CALL_KINDS)[number];
export const AI_CALL_KIND_LABELS: Readonly<Record<AiCallKind, string>> = {
  summary: 'Meeting summary',
  slack_judge: 'Slack judge',
  slack_analysis: 'Slack analysis',
  assistant_brief: 'Assistant brief',
  // Shared work (ADR 0014): the row names no person — the call compares several people's to-dos.
  todo_link: 'Linking to-dos (shared)',
  slack_sweep: 'Daily Slack sweep',
  fellow_match: "Matching Fellow's items",
  project_sort: 'Sorting to-dos into projects',
  // Shared work (design §2.8): the row names no person — the report reads many members' material for the executives.
  executive_report: 'Executive report (shared)',
};

/**
 * How an AI call ended. A call the provider answered is billed whatever came back, so `invalid_output`
 * (an answer Calis refused) and `error` (the provider's own failure) are recorded beside `ok`; `timeout`
 * is a call that got no answer in time.
 */
export const AI_CALL_OUTCOMES = ['ok', 'invalid_output', 'error', 'timeout'] as const;
export type AiCallOutcome = (typeof AI_CALL_OUTCOMES)[number];
/** 05-content §6: finished well is Completed, finished badly is Failed; the two named failures are their own facts. */
export const AI_CALL_OUTCOME_LABELS: Readonly<Record<AiCallOutcome, string>> = {
  ok: 'Completed',
  invalid_output: 'Invalid output',
  error: 'Failed',
  timeout: 'Timed out',
};

/**
 * Why a failed call got no answer from the API, as `ai_calls.error_type` records it when there is no
 * API error type to record (migration 0013): the deadline passed (`timeout`), the connection failed or
 * broke off (`connection`), Calis's own code failed around the call (`local`), the process was stopping
 * and cut the call short (`shutdown`: a deploy handed the job back, ADR 0013 — never an AI timeout), the worker stood
 * down in a process that kept running because it no longer held the instance lease and cut the call short
 * (`lease_lost`, 2026-10-16: migration 0060 — a lapse or a takeover, no deploy), or
 * nothing more is known (`unknown`). A call the API refused records the API's own `error.type` instead — an open set the
 * vendor extends (`invalid_request_error`, `rate_limit_error`, `overloaded_error`, …), kept as sent when
 * it is a plain lowercase name, like `stop_reason`.
 */
// Why `lease_lost` apart from `shutdown`: a `shutdown` row is matched to a deploy or a restart, and a lease stand-down
// is neither — the same process may take the lease back seconds later (src/server/jobs/stop-reason.ts). No migration:
// 0013's CHECK admits any short lowercase word.
export const AI_CALL_FAILURE_REASONS = ['timeout', 'connection', 'local', 'unknown', 'shutdown', 'lease_lost'] as const;
export type AiCallFailureReason = (typeof AI_CALL_FAILURE_REASONS)[number];

/**
 * What a ledger row points at (`target_id` is that record's id, never its content). `executive_report` (migration 0053;
 * ADR 0019): the organisation report a report call wrote, by its id.
 */
export const AI_CALL_TARGET_TYPES = ['meeting', 'slack_analysis', 'assistant_brief', 'slack_sweep', 'executive_report'] as const;
export type AiCallTargetType = (typeof AI_CALL_TARGET_TYPES)[number];

/** How the Costs page breaks the calls down: by what they were for, by model, or by the person they were for. */
export const AI_CALL_FACETS = ['kind', 'model', 'person'] as const;
export type AiCallFacet = (typeof AI_CALL_FACETS)[number];
export const AI_CALL_FACET_LABELS: Readonly<Record<AiCallFacet, string>> = {
  kind: 'By type',
  model: 'By model',
  person: 'By person',
};

// --- Audit -------------------------------------------------------------------------------------

export const AUDIT_SURFACES = ['web', 'worker', 'scheduler', 'cli'] as const;
export type AuditSurface = (typeof AUDIT_SURFACES)[number];
export const AUDIT_SURFACE_LABELS: Readonly<Record<AuditSurface, string>> = {
  web: 'Web',
  worker: 'Background worker',
  scheduler: 'Scheduler',
  cli: 'Command line',
};

// `skipped` (migration 0050): a run that looked and did nothing, and a request that started nothing (06-modules §6.5).
export const AUDIT_OUTCOMES = ['success', 'refused', 'failed', 'skipped'] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];
/** 05-content §6: finished well is Completed, finished badly is Failed; a refusal is its own fact; looked and did nothing is Skipped. */
export const AUDIT_OUTCOME_LABELS: Readonly<Record<AuditOutcome, string>> = {
  success: 'Completed',
  refused: 'Refused',
  failed: 'Failed',
  skipped: 'Skipped',
};

/**
 * The env-derived superuser's two changes, as the durable `superuser_audit` table records them (migration 0049;
 * permission model 05-administration §3 "Reaches the superuser") — the general trail's operation names, unchanged.
 */
export const SUPERUSER_TRAIL_ACTIONS = ['subject.superuser.grant', 'subject.superuser.revoke'] as const;
export type SuperuserTrailAction = (typeof SUPERUSER_TRAIL_ACTIONS)[number];

// --- Deployment configuration visible to the client --------------------------------------------

export const APP_ENVS = ['development', 'test', 'production'] as const;
export type AppEnv = (typeof APP_ENVS)[number];
export const APP_ENV_LABELS: Readonly<Record<AppEnv, string>> = {
  development: 'Development',
  test: 'Test',
  production: 'Production',
};

export const AUTH_MODES = ['google', 'dev'] as const;
export type AuthMode = (typeof AUTH_MODES)[number];

export const FELLOW_MODES = ['live', 'fixture', 'off'] as const;
export type FellowMode = (typeof FELLOW_MODES)[number];
export const FELLOW_MODE_LABELS: Readonly<Record<FellowMode, string>> = {
  live: 'Fellow',
  fixture: 'Demo Fellow data',
  off: 'Not configured',
};

export const AI_PROVIDERS = ['anthropic', 'offline', 'off'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];
export const AI_PROVIDER_LABELS: Readonly<Record<AiProvider, string>> = {
  anthropic: 'Anthropic Claude',
  offline: 'Demo summariser',
  off: 'Not configured',
};

/**
 * The effort levels the Claude API accepts in `output_config.effort`, lowest first (checked against the
 * API reference on 2026-09-27). One level serves every AI call Calis makes (`ANTHROPIC_EFFORT`).
 */
export const ANTHROPIC_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type AnthropicEffort = (typeof ANTHROPIC_EFFORTS)[number];

export const SLACK_MODES = ['live', 'fixture', 'off'] as const;
export type SlackMode = (typeof SLACK_MODES)[number];
export const SLACK_MODE_LABELS: Readonly<Record<SlackMode, string>> = {
  live: 'Slack',
  fixture: 'Demo Slack data',
  off: 'Not configured',
};

export const LOG_LEVELS = ['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent'] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];

// --- Sign-in outcomes shown on /signed-out ------------------------------------------------------

export const SIGNED_OUT_REASONS = ['signed_out', 'not_allowed', 'pending', 'expired', 'error'] as const;
export type SignedOutReason = (typeof SIGNED_OUT_REASONS)[number];
export const SIGNED_OUT_REASON_LABELS: Readonly<Record<SignedOutReason, string>> = {
  signed_out: 'You have signed out.',
  not_allowed: 'This account cannot sign in to Calis. Use your Centro Google Workspace account.',
  pending: 'Your access is waiting for an administrator to approve it.',
  expired: 'Your sign-in took too long.',
  error: 'Sign-in could not be completed.',
};

// --- API error codes ---------------------------------------------------------------------------

export const API_ERROR_CODES = [
  'invalid_request',
  'unauthenticated',
  'forbidden',
  'csrf_required',
  'feature_not_configured',
  'not_found',
  'conflict',
  'self_lockout',
  'superuser_env_managed',
  'group_required',
  'sign_in_pending',
  'rate_limited',
  'not_ready',
  'upstream_unavailable',
  'internal_error',
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

// --- Projects (migration 0047; owner rulings of 2026-10-02, escalation 20261002-144639; ADR 0017) --------------

/**
 * Who gave a project its present name (`projects.named_by`) and who filed a membership (`todo_projects`,
 * `meeting_projects`, `finding_projects` and `matter_projects`' `set_by`): the model, or the person. Why one list for
 * both: the fence sends every live name whoever named it, and "the AI's rows are replaced by the AI's next answer, a
 * person's stand" is decided by this value on every membership table alike.
 */
export const PROJECT_SET_BY = ['ai', 'person'] as const;
export type ProjectSetBy = (typeof PROJECT_SET_BY)[number];
/** On a project row: who named it. */
export const PROJECT_SET_BY_LABELS: Readonly<Record<ProjectSetBy, string>> = {
  ai: 'Named by Calis',
  person: 'Named by you',
};
/** On a membership — a to-do, meeting, finding or matter filed under a project: who filed it. */
export const PROJECT_FILED_BY_LABELS: Readonly<Record<ProjectSetBy, string>> = {
  ai: 'Filed by Calis',
  person: 'Filed by you',
};

/**
 * Why a project reference the model returned was not kept (ai/projects/validate.ts): counts only, on the delivery's
 * audit row (`after.projects.dropped`), never shown one by one. A handle no fence sent; a new name that links,
 * addresses or identifies; a new name that is a person's name — the owner's or an attendee's, token-wise
 * (src/shared/projects.ts `namesAPerson`); a name past 60 characters; one blank once keyed; a second reference to the
 * same project on one item; or the person's own "not this one" tombstone standing against the pair. No CHECK holds
 * these, so T06 has no row for them.
 */
export const PROJECT_REF_DROP_REASONS = ['unknown_handle', 'unsafe_text', 'names_a_person', 'too_long', 'blank', 'duplicate', 'opted_out'] as const;
export type ProjectRefDropReason = (typeof PROJECT_REF_DROP_REASONS)[number];

/** The Projects list's sortable columns (`GET /api/projects?sort=`); `last_used_at`, newest first, is the default. */
export const PROJECT_SORT_KEYS = ['name', 'meetings', 'open_todos', 'last_used_at', 'named_by'] as const;
export type ProjectSortKey = (typeof PROJECT_SORT_KEYS)[number];

/** A row of the Projects list: the person's own project, or one a colleague shares with them. DTO only; no column. */
export const PROJECT_KINDS = ['own', 'shared'] as const;
export type ProjectKind = (typeof PROJECT_KINDS)[number];

/**
 * Where a share stands, derived from `project_shares` at every read and never stored (DTO only): `standing` — offered
 * and open; `merged_in` — the recipient grouped it under a project of their own (S1; S6: their consent to a merge-back);
 * `merged_back` — the sharer groups the recipient's own rows under theirs as well; `left` — the recipient left the
 * shared view; `revoked` — the sharer ended it (S7: "Sharer only can revoke"); `inactive` — the other party is not
 * admitted at this read, so nothing crosses until they are.
 */
export const PROJECT_SHARE_STATES = ['standing', 'merged_in', 'merged_back', 'left', 'revoked', 'inactive'] as const;
export type ProjectShareState = (typeof PROJECT_SHARE_STATES)[number];
/**
 * The state word on a share's pill (design system 05-content §6: Active, Inactive, Cancelled). Merged in, merged back,
 * left and "by you" are meta text beside the pill, never a state word of their own.
 */
export const PROJECT_SHARE_STATE_LABELS: Readonly<Record<ProjectShareState, string>> = {
  standing: 'Active',
  merged_in: 'Active',
  merged_back: 'Active',
  left: 'Cancelled',
  revoked: 'Cancelled',
  inactive: 'Inactive',
};

// --- Integration runs (design system 06-modules §6, binding from v8.3.0; conformity Phase 2 group A, 2026-10-05) ----

/** The systems whose runs the Connections page records: the Fellow sync and the Slack channel-list refresh. */
export const CONNECTION_SYSTEMS = ['fellow', 'slack'] as const;
export type ConnectionSystem = (typeof CONNECTION_SYSTEMS)[number];
export const CONNECTION_SYSTEM_LABELS: Readonly<Record<ConnectionSystem, string>> = {
  fellow: 'Fellow',
  slack: 'Slack',
};

/**
 * How a run began (06-modules §6.4 Trigger: "worded as how the run began, never with a §6 word"): the schedule's tick,
 * a person's act — Sync now, Refresh channel list, connecting, switching direct messages back on — or the command line
 * (the demo seed). A job's own `trigger` (`manual` / `connect` / `settings`) maps to `hand`: each is the person's hand.
 */
export const CONNECTION_RUN_TRIGGERS = ['schedule', 'hand', 'cli'] as const;
export type ConnectionRunTrigger = (typeof CONNECTION_RUN_TRIGGERS)[number];
export const CONNECTION_RUN_TRIGGER_LABELS: Readonly<Record<ConnectionRunTrigger, string>> = {
  schedule: 'By the schedule',
  hand: 'By hand',
  cli: 'From the command line',
};

/**
 * The state of one run (05-content §6; 06-modules §6.4 State): Running, Completed, Failed, Skipped — and Refused where
 * the permission model or a floor stopped it (05-content §6: "Failed would say the run broke"; 04-enforcement §8 shows a
 * refusal honestly). A stored code reaches the screen only as its word here.
 */
export const CONNECTION_RUN_STATES = ['running', 'completed', 'failed', 'skipped', 'refused'] as const;
export type ConnectionRunState = (typeof CONNECTION_RUN_STATES)[number];
export const CONNECTION_RUN_STATE_LABELS: Readonly<Record<ConnectionRunState, string>> = {
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
  refused: 'Refused',
};

// --- Client error reports (POST /api/client-errors; app-baseline 05-client §5.2; conformity D2, 2026-10-05) -----------

/**
 * Where a client error report came from: a region boundary catching a render (`render`), the window's `error` event
 * (`error`) or an `unhandledrejection` (`unhandledrejection`). The bounds and the reference pattern the report shares
 * between client and server live in `shared/client-errors.ts`; the kinds are declared here, as every closed enum is.
 */
export const CLIENT_ERROR_KINDS = ['render', 'error', 'unhandledrejection'] as const;
export type ClientErrorKind = (typeof CLIENT_ERROR_KINDS)[number];

// --- Executive intelligence (migration 0053; the owner's request of 2026-10-04 and his picks E1–E16, escalation ------
// --- 20261005-000818-executive-intelligence-an-organisation-report-sh; ADR 0019) ------------------------------------
// Why the closed lists land before anything reads or writes them (design §6, stage 1): the schema's CHECKs in 0053 mirror
// them (T06 pins each pair), so the tables exist inert while EXECUTIVE_MODE is off. Every value is an identifier; no list
// here carries a word a model wrote — a climate is one of four words and a signal one of seven, never free text (E3).

/**
 * The climate of a discussion as a whole (design §2.5): calm (routine, agreed), mixed (some open friction or worry),
 * strained (unresolved disagreement, pressure or slippage voiced), tense (conflict, escalation or distress voiced). Asked
 * of the summary, the daily Slack look and the Slack check only while `floors.executive` holds; stored on
 * `summaries.discussion_climate` and `slack_climate_marks.climate`. It describes a conversation, never a person.
 */
export const DISCUSSION_CLIMATES = ['calm', 'mixed', 'strained', 'tense'] as const;
export type DiscussionClimate = (typeof DISCUSSION_CLIMATES)[number];

/**
 * What a discussion itself shows, at most four a discussion (design §2.5; the four is enforced where the answer is
 * validated, since a strict schema carries no `maxItems`). Held in JSON arrays (`climate_signals_json`), so no CHECK lists
 * them and T06 has no row for them.
 */
export const CLIMATE_SIGNALS = [
  'deadline_pressure',
  'unclear_ownership',
  'blocked_dependency',
  'disagreement_unresolved',
  'resourcing',
  'customer_risk',
  'morale_concern',
] as const;
export type ClimateSignal = (typeof CLIMATE_SIGNALS)[number];
/**
 * How a signal reads beside a climate word on the person's own meeting page and Slack today tab (design §2.5: "Climate:
 * strained — deadline pressure, unclear ownership"). Lower case: each is a phrase inside a sentence, joined by the
 * locale's list (formatList), never a heading. The climate words themselves are shown as they are — a reading, worded
 * with its qualifier ("Climate:"), never a state word (design system 05-content §6.1).
 */
export const CLIMATE_SIGNAL_LABELS: Readonly<Record<ClimateSignal, string>> = {
  deadline_pressure: 'deadline pressure',
  unclear_ownership: 'unclear ownership',
  blocked_dependency: 'a blocked dependency',
  disagreement_unresolved: 'unresolved disagreement',
  resourcing: 'resourcing',
  customer_risk: 'customer risk',
  morale_concern: 'morale concern',
};

/** Which Slack look a wordless climate mark came from (`slack_climate_marks.kind`): the daily sweep or a meeting's check. */
export const SLACK_CLIMATE_MARK_KINDS = ['sweep', 'check'] as const;
export type SlackClimateMarkKind = (typeof SLACK_CLIMATE_MARK_KINDS)[number];

/**
 * Where one organisation report stands (`executive_reports.status`): waiting, being written, written (`ready`, the only
 * state holding a payload), withheld (`insufficient`: since the owner's ruling of 2026-10-12, "Remove them everywhere", a
 * window with no meeting to write from — 0 people, 0 meetings and its Slack climate, before the call or after the
 * after-gate struck every member it read; until then below E5's thresholds, with the two counts it fell short with), or
 * `failed`.
 */
export const EXECUTIVE_REPORT_STATUSES = ['queued', 'running', 'ready', 'insufficient', 'failed'] as const;
export type ExecutiveReportStatus = (typeof EXECUTIVE_REPORT_STATUSES)[number];

/** How often the scheduled report is written (EXECUTIVE_REPORT_CADENCE; the owner's pick E8: daily). */
export const EXECUTIVE_REPORT_CADENCES = ['daily', 'weekly'] as const;
export type ExecutiveReportCadence = (typeof EXECUTIVE_REPORT_CADENCES)[number];

/** What queued a report: the scheduler's tick, or an executive's "Refresh now". */
export const EXECUTIVE_REPORT_TRIGGERS = ['schedule', 'manual'] as const;
export type ExecutiveReportTrigger = (typeof EXECUTIVE_REPORT_TRIGGERS)[number];

/** What one item of the report is (design §2.6): a theme, a risk, a blocker, a decision still open, or an opportunity. */
export const EXECUTIVE_THEME_KINDS = ['theme', 'risk', 'blocker', 'decision_pending', 'opportunity'] as const;
export type ExecutiveThemeKind = (typeof EXECUTIVE_THEME_KINDS)[number];

/** How a theme moved against the previous report (design §2.6). */
export const EXECUTIVE_TRENDS = ['new', 'rising', 'steady', 'easing', 'resolved'] as const;
export type ExecutiveTrend = (typeof EXECUTIVE_TRENDS)[number];

/** The verdict on each theme of the previous report and each open shared matter (design §2.6, `prior_review`). */
export const EXECUTIVE_PRIOR_VERDICTS = ['still_holds', 'resolved', 'overtaken', 'unclear'] as const;
export type ExecutivePriorVerdict = (typeof EXECUTIVE_PRIOR_VERDICTS)[number];

/**
 * How sure the model is of one theme (design §2.6: "the model's closed word plus evidence_meetings/evidence_owners, both
 * shown"). Held in the report's payload JSON, so no CHECK lists it and T06 has no row for it.
 */
export const EXECUTIVE_CONFIDENCES = ['high', 'medium', 'low'] as const;
export type ExecutiveConfidence = (typeof EXECUTIVE_CONFIDENCES)[number];

/** Where a shared executive matter came from (`executive_matters.kind`; E10): a report's action item, or an executive's own. */
export const EXECUTIVE_MATTER_KINDS = ['report', 'manual'] as const;
export type ExecutiveMatterKind = (typeof EXECUTIVE_MATTER_KINDS)[number];

/** One executive's own word on a shared matter (`executive_matter_states.status`; E10); a missing row reads `open`. */
export const EXECUTIVE_MATTER_STATUSES = ['open', 'done', 'dismissed'] as const;
export type ExecutiveMatterStatus = (typeof EXECUTIVE_MATTER_STATUSES)[number];

/**
 * Why an item the model returned was not kept (design §2.10, drop, never repair): counts only, on the report row's
 * `dropped_json` and its audit row, never the dropped text. No CHECK holds these, so T06 has no row for them.
 * `too_few_owners` — since the owner's ruling of 2026-10-12 ("Remove them everywhere", "Names only where three") — is
 * asked only where an item would place a shared project's name on fewer than three contributors (permission model 1.3.0
 * §9.1 condition 5): a theme or an action whose own words name one. A row written before then may count it for any theme
 * resting on fewer than three owners who were not readers (E5, superseded).
 */
export const EXECUTIVE_DROP_REASONS = [
  'unknown_ref',
  'too_few_owners',
  'contributor_lapsed',
  'names_a_person',
  'unknown_project',
  'unsafe_text',
  'verbatim_copy',
  'too_long',
  'blank',
  'duplicate',
] as const;
export type ExecutiveDropReason = (typeof EXECUTIVE_DROP_REASONS)[number];

/**
 * Why a report counted no Slack climate, as a report written now records it (the owner's ruling of 2026-10-09 on ADR 0019's
 * Open 45, "Say why Slack wasn't counted"): `slack_off` — Slack climate was off on the server that wrote it (the Slack
 * check's floor unmet, or no Slack analyser configured), so no member's marks were read; `no_marks` — it was on, and no
 * member had a Slack climate mark in the window that the report could count (since the owner's ruling of 2026-10-13 on Open
 * 56, "Count them too", every admitted member who holds both Slack grants and has Slack connected is read, AI summaries or
 * not). Decided by the executor as it finishes the report: a written report keeps it in its payload JSON (`slackUncounted`),
 * and — since his ruling of 2026-10-13 on Opens 50 and 54, "Record the reasons" — a withheld one, which has no payload, in
 * `executive_reports.slack_uncounted` (migration 0059), whose CHECK is this list (T06). One place a report: never both.
 */
export const EXECUTIVE_SLACK_UNCOUNTED_RECORDED = ['slack_off', 'no_marks'] as const;
export type ExecutiveSlackUncountedRecorded = (typeof EXECUTIVE_SLACK_UNCOUNTED_RECORDED)[number];

/**
 * Every reason a written report's payload may hold for an uncounted Slack climate: the two above, and `below_minimum`, never
 * written since the owner's ruling of 2026-10-12, "Count whoever is connected" — it stays in the list only so a report
 * written before then (when fewer than EXECUTIVE_MIN_CONTRIBUTORS members who were not executives had a mark) is still read
 * back as it was kept. In the payload alone, so no CHECK lists it and T06 has no row for it. A reason, never a count.
 */
export const EXECUTIVE_SLACK_UNCOUNTED_REASONS = [...EXECUTIVE_SLACK_UNCOUNTED_RECORDED, 'below_minimum'] as const;
export type ExecutiveSlackUncounted = (typeof EXECUTIVE_SLACK_UNCOUNTED_REASONS)[number];

/**
 * Why a report was withheld (`status = 'insufficient'`: no report written, no AI call kept), one word for each way the
 * executor withholds (src/server/executive/report-job.ts) — the owner's ruling of 2026-10-13 on ADR 0019's Open 50, "Record
 * the reasons", so a page states the cause and not the rule: `no_meetings` — M1 found no new meeting summary in the window
 * to write from (before any call); `contributors_lapsed` — every member whose meetings were read lost their access or AI
 * summaries while the report was being written, so none of what was read may be kept; `sent_contributors_lapsed` — every
 * member whose meetings were SENT lost it, and the meetings left were ones the input budget had left out for length, so the
 * answer rests on nothing the report may keep. Kept in `executive_reports.withheld_reason` (migration 0059, its CHECK this
 * list: T06) and on the `executive.report` audit row's `reason`. NULL on a report withheld before 0059 — whose cause was
 * never recorded, and may be a minimum since removed (E5): the page then states the rule and claims no cause.
 */
export const EXECUTIVE_WITHHELD_REASONS = ['no_meetings', 'contributors_lapsed', 'sent_contributors_lapsed'] as const;
export type ExecutiveWithheldReason = (typeof EXECUTIVE_WITHHELD_REASONS)[number];

/**
 * Why a written report holds no meeting-climate count (the owner's ruling of 2026-10-13 on ADR 0019's Open 54, "Record the
 * reasons": "a closed reason for an uncounted climate"): `no_words` — none of the meetings it counted carried a climate word
 * (a summary made while executive intelligence was off, or whose word slipped). Since the owner's ruling of 2026-10-12 it is
 * the one cause; a report written before may have been held under the climate's own minimum, since removed, and recorded
 * none — the page then states the rule and claims no cause. Held in the written report's payload (`climateUncounted`),
 * beside `slackUncounted`, so no CHECK lists it and T06 has no row for it. A reason, never a count.
 */
export const EXECUTIVE_CLIMATE_UNCOUNTED_REASONS = ['no_words'] as const;
export type ExecutiveClimateUncounted = (typeof EXECUTIVE_CLIMATE_UNCOUNTED_REASONS)[number];

/**
 * Why an operator withdrew a stored report to have its window reported again (`executive:rereport`,
 * src/server/executive/rereport.ts) — the owner's ruling of 2026-10-16 on the stray instance's reports, "Remove and
 * re-report": `stray_instance` — a second Calis process on the production database (container `deploy-calis-1`,
 * 2026-10-09 to 2026-10-16) wrote the report under an older build's rules. The operator states one; the verb refuses any
 * other word. Kept only on the `executive.report.withdraw` audit row's `reason` (no column holds it, so no CHECK and no T06
 * row). A reason, never a count or a name.
 */
export const EXECUTIVE_REPORT_WITHDRAW_REASONS = ['stray_instance'] as const;
export type ExecutiveReportWithdrawReason = (typeof EXECUTIVE_REPORT_WITHDRAW_REASONS)[number];

// --- Executive intelligence: the words the page shows (ADR 0019, design §2.13; stage 6) ------------------------------
// Why here and not in the page: a closed list's display labels live beside the list (CLAUDE.md, "one place a closed enum
// is declared"). Why none of the readings is a 05-content §6 word: a theme's trend, the model's confidence and its verdict
// on an earlier theme are what a model judged — readings (§6.1, ruled A25 in design system 8.5.0) — each shown under a
// column or a qualifier naming what is read, never in one column with a state; so `resolved` reads "Settled" and the
// verdict "Settled by later discussion" (the Slack check's own wording), and `new` reads "First seen", "New" being on the
// Not-list of Open and Draft. An executive's own word on a shared matter IS a state of that matter for them, so it takes
// §6's words, as a to-do's does: Open, Completed, Dismissed.

/** A climate word as a tile's label (design §2.13: "four climate StatTiles"). Capitalised: each stands alone as a label. */
export const DISCUSSION_CLIMATE_LABELS: Readonly<Record<DiscussionClimate, string>> = {
  calm: 'Calm',
  mixed: 'Mixed',
  strained: 'Strained',
  tense: 'Tense',
};

/** A warning signal as a table row names it — capitalised, the row's own label (the lower-case forms sit inside sentences). */
export const CLIMATE_SIGNAL_TITLES: Readonly<Record<ClimateSignal, string>> = {
  deadline_pressure: 'Deadline pressure',
  unclear_ownership: 'Unclear ownership',
  blocked_dependency: 'A blocked dependency',
  disagreement_unresolved: 'Unresolved disagreement',
  resourcing: 'Resourcing',
  customer_risk: 'Customer risk',
  morale_concern: 'Morale concern',
};

/** What one item of the report is, as the Kind column says it. `decision_pending` reads as a category, never as a wait. */
export const EXECUTIVE_THEME_KIND_LABELS: Readonly<Record<ExecutiveThemeKind, string>> = {
  theme: 'Theme',
  risk: 'Risk',
  blocker: 'Blocker',
  decision_pending: 'Decision to make',
  opportunity: 'Opportunity',
};

/** How a theme moved against the report it was compared with — a reading, under the Trend column. */
export const EXECUTIVE_TREND_LABELS: Readonly<Record<ExecutiveTrend, string>> = {
  new: 'First seen',
  rising: 'Rising',
  steady: 'Steady',
  easing: 'Easing',
  resolved: 'Settled',
};

/** The model's confidence in a theme — a reading, under the Confidence column. */
export const EXECUTIVE_CONFIDENCE_LABELS: Readonly<Record<ExecutiveConfidence, string>> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** The verdict on an earlier theme or an open matter (design §2.6 `prior_review`) — a reading, as the Slack check's are worded. */
export const EXECUTIVE_PRIOR_VERDICT_LABELS: Readonly<Record<ExecutivePriorVerdict, string>> = {
  still_holds: 'Still holds',
  resolved: 'Settled by later discussion',
  overtaken: 'Overtaken by later discussion',
  unclear: 'Unclear',
};

/** One executive's own word on a shared matter (05-content §6, as a to-do's: Open, Completed, Dismissed). */
export const EXECUTIVE_MATTER_STATUS_LABELS: Readonly<Record<ExecutiveMatterStatus, string>> = {
  open: 'Open',
  done: 'Completed',
  dismissed: 'Dismissed',
};

/** How a report began (06-modules §6.4, *Trigger*: worded as how the run began, never with a §6 word). */
export const EXECUTIVE_REPORT_TRIGGER_LABELS: Readonly<Record<ExecutiveReportTrigger, string>> = {
  schedule: 'By the schedule',
  manual: 'By hand (Refresh now)',
};
